import CONSTANTS from '@/constants'
import { IProductExtended, IPlan } from '@/@types'
import { isSubscriptionPlan } from '@/helpers/productClassification'

type CartPlanPayload = {
  code: string
  trial: boolean
  add_ons: string[]
}

type CartItemPayload = {
  code: string
  quantity?: number
}

export type CartPayload = {
  plans: CartPlanPayload[]
  items: CartItemPayload[]
  bundle?: {
    slug?: string
    voucher?: string
    skill_dive_area?: string
  }
}

type BuildCartPayloadParams = {
  shoppingCart?: IProductExtended[]
  selectedPlan?: Partial<IProductExtended>
  selectedSkillDivePlan?: Partial<IProductExtended>
  availablePlans?: Array<IProductExtended | IPlan>
  isPromotionActive?: boolean
}

const {
  PLANS,
  BLACK_FRIDAY_PRODUCT_BUNDLES,
  LEAP_YEAR_PROMO_PRODUCT_BUNDLES,
  TCS_PROMO_PRODUCT_BUNDLES,
  SUMMER_SAVINGS_BUNDLE,
} = CONSTANTS

const getBundleIdentifiers = (bundleGroup: Record<string, unknown>) => {
  return Object.values(bundleGroup).filter((value): value is string => typeof value === 'string')
}

const promotionBundleIdentifiers = [
  ...getBundleIdentifiers(BLACK_FRIDAY_PRODUCT_BUNDLES),
  ...getBundleIdentifiers(LEAP_YEAR_PROMO_PRODUCT_BUNDLES),
  ...getBundleIdentifiers(TCS_PROMO_PRODUCT_BUNDLES),
  ...getBundleIdentifiers(SUMMER_SAVINGS_BUNDLE),
]

const mapAddOns = (addOns: Array<{ merchant_id?: string }> = []) => {
  return addOns
    .map(addOn => addOn?.merchant_id)
    .filter((merchantId): merchantId is string => typeof merchantId === 'string' && !!merchantId)
}

const sanitizeQuantity = (value: unknown) => {
  const quantity = Number(value)
  if (Number.isFinite(quantity) && quantity > 0) {
    return quantity
  }
  return undefined
}

export const buildCartPayload = (params: BuildCartPayloadParams): CartPayload => {
  const {
    shoppingCart = [],
    selectedPlan = {},
    selectedSkillDivePlan = {},
    availablePlans = [],
    isPromotionActive = false,
  } = params

  const plans: CartPlanPayload[] = []

  if (selectedPlan?.merchant_id) {
    plans.push({
      code: selectedPlan.merchant_id,
      trial: selectedPlan.plan_trial ?? false,
      add_ons:
        selectedPlan.merchant_id === PLANS.PREMIUM_PLUS ? mapAddOns(selectedPlan.addOnsOnCart) : [],
    })
  }

  if (selectedSkillDivePlan?.merchant_id) {
    plans.push({
      code: selectedSkillDivePlan.merchant_id,
      trial: false,
      add_ons: mapAddOns(selectedSkillDivePlan.addOnsOnCart),
    })
  }

  const items: CartItemPayload[] = shoppingCart
    .filter(item => item?.merchant_id && !isSubscriptionPlan(item))
    .map(item => {
      const payload: CartItemPayload = {
        code: item.merchant_id!,
      }

      const quantity = sanitizeQuantity((item as { quantity?: number }).quantity)
      if (quantity) {
        payload.quantity = quantity
      }
      return payload
    })

  const payload: CartPayload = { plans, items }

  if (isPromotionActive) {
    const promoBundle = shoppingCart.find(
      item => item?.merchant_id && promotionBundleIdentifiers.includes(item.merchant_id)
    )

    if (promoBundle) {
      const itemsWithoutBundle = items.filter(
        item => !promotionBundleIdentifiers.includes(item.code)
      )

      if (promoBundle.merchant_id === BLACK_FRIDAY_PRODUCT_BUNDLES.INE_BLACK_FRIDAY_BUNDLE_THREE) {
        const fundamentals = availablePlans.find(plan => plan.merchant_id === PLANS.ANNUAL)
        if (fundamentals?.merchant_id) {
          payload.plans = [
            {
              code: fundamentals.merchant_id,
              trial: fundamentals.plan_trial ?? false,
              add_ons: [],
            },
          ]
          payload.items = itemsWithoutBundle
        }
      } else if (promoBundle.bundle) {
        const bundleDetails = promoBundle.bundle as {
          slug?: string
          voucher?: string
          skill_dive_area?: string
        }
        payload.bundle = {
          slug: bundleDetails?.slug || promoBundle.slug,
          voucher: bundleDetails?.voucher,
          skill_dive_area: bundleDetails?.skill_dive_area,
        }
        payload.items = itemsWithoutBundle
      } else {
        payload.items = itemsWithoutBundle
      }
    }
  }

  return payload
}

export default buildCartPayload
